import { useState, useEffect } from 'react';
import { Button } from     '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/InputDefault';
import { Checkbox } from '@/components/ui/checkbox';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { updateGameInfo } from '@/lib/stores/gameStore';
import IconProvider from '@/components/ui/IconProvider';
import { ExportTemplate, ExportTemplatesConfig } from '@/types/exportTemplates';
import { getDatabasePresetForGame } from '@/lib/integrations/exportTemplatePresets';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { useGlossaryData } from '@/lib/useObjectsData';
import { renderDatabaseTemplate } from '@/lib/rerun-manager/templateEngine';
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useToast } from '@/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/DefaultSelectDropdown";
import { useTypesDataContext } from '@/providers/TypesDataProvider';
import { useTagsDataContext } from '@/providers/TagsDataProvider';

type TabType = 'config' | 'categories' | 'fields' | 'output';

// Helper function to get default export value based on field type
function getDefaultExportValue(fieldType: string): string {
  switch (fieldType) {
    case 'glossaryItem':
    case 'types':
    case 'tags':
      return 'name:original';
    case 'file':
    case 'image':
      return 'url';
    case 'title':
    case 'text':
    case 'textarea':
    case 'richtext':
      return 'value:original';
    case 'number':
    case 'boolean':
    case 'date':
      return 'value';
    case 'select':
    case 'multiselect':
      return 'value';
    default:
      return 'value';
  }
}

// Helper function to get display name for field types
function getFieldTypeDisplayName(fieldType: string): string {
  if (fieldType === 'glossaryItem') return 'object';
  return fieldType;
}

// Helper function to get available export options based on field type
function getExportOptions(fieldType: string): Array<{ value: string; label: string }> {
  switch (fieldType) {
    case 'glossaryItem':
    case 'types':
    case 'tags':
      return [
        { value: 'name:original', label: 'Name (As Written)' },
        { value: 'name:uppercase', label: 'Name (UPPERCASE)' },
        { value: 'name:lowercase', label: 'Name (lowercase)' },
        { value: 'id', label: 'ID' },
      ];
    case 'file':
    case 'image':
      return [
        { value: 'url', label: 'URL' },
        { value: 'name:original', label: 'Name (As Written)' },
        { value: 'name:uppercase', label: 'Name (UPPERCASE)' },
        { value: 'name:lowercase', label: 'Name (lowercase)' },
        { value: 'id', label: 'ID' },
      ];
    case 'title':
    case 'text':
    case 'textarea':
    case 'richtext':
      return [
        { value: 'value:original', label: 'Text (As Written)' },
        { value: 'value:uppercase', label: 'Text (UPPERCASE)' },
        { value: 'value:lowercase', label: 'Text (lowercase)' },
      ];
    case 'boolean':
      return [
        { value: 'value', label: 'Boolean (true/false)' },
        { value: 'number', label: 'Number (1/0)' },
      ];
    case 'select':
    case 'multiselect':
      return [
        { value: 'value', label: 'Value' },
        { value: 'label', label: 'Label' },
        { value: 'id', label: 'ID' },
      ];
    default:
      return [
        { value: 'value', label: 'Value' },
        { value: 'string', label: 'String' },
      ];
  }
}

export default function DatabaseSection() {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedGame, refreshData } = useSimplifiedUserData();
  const { items: allItems } = useGlossaryData({
    gameId: selectedGame?.id,
    type: 'library',  // Get library objects (not documents)
    autoLoad: true
  });
  const { glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { types } = useTypesDataContext();
  const { tags } = useTagsDataContext();
  const [template, setTemplate] = useState<ExportTemplate>(() => getDatabasePresetForGame(selectedGame));
  const [isSaving, setIsSaving] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('config');
  const [selectedPreviewItems, setSelectedPreviewItems] = useState<string[]>([]);
  const [previewOutput, setPreviewOutput] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [selectedCategoryForMapping, setSelectedCategoryForMapping] = useState<string>('');
  const [categoryFieldMappings, setCategoryFieldMappings] = useState<Record<string, Record<string, string>>>({});
  const [previewItemForMapping, setPreviewItemForMapping] = useState<string>('');

  useEffect(() => {
    if (selectedGame?.settings?.exportTemplates) {
      const config = selectedGame.settings.exportTemplates as ExportTemplatesConfig;
      
      // Get the appropriate preset for this game's engine
      const defaultPreset = getDatabasePresetForGame(selectedGame);
      const presetId = defaultPreset.id;
      
      const existingTemplate = config.templates?.find(t => t.id === presetId);
      if (existingTemplate) {
        setTemplate(existingTemplate);
        // Load category field mappings if they exist
        if ((existingTemplate as any).categoryFieldMappings) {
          setCategoryFieldMappings((existingTemplate as any).categoryFieldMappings);
        }
      } else {
        // Use the default preset for this engine
        setTemplate(defaultPreset);
      }
      setIsActive(config.activeTemplateId === presetId);
    } else {
      // No config yet, use default preset for this game's engine
      const defaultPreset = getDatabasePresetForGame(selectedGame);
      setTemplate(defaultPreset);
    }
  }, [selectedGame]);

  const updateField = (field: keyof ExportTemplate, value: any) => {
    setTemplate(prev => ({ ...prev, [field]: value }));
  };

  // Generate preview when items or template changes
  useEffect(() => {
    if (selectedPreviewItems.length > 0 && allItems && glossaryBlueprints) {
      const itemsToPreview = allItems.filter(item => selectedPreviewItems.includes(item.id));
      if (itemsToPreview.length > 0) {
        try {
          // Include categoryFieldMappings in the template for preview
          const templateWithMappings = {
            ...template,
            categoryFieldMappings
          };
          // Pass types, tags, and all items for resolving reference fields
          const preview = renderDatabaseTemplate(templateWithMappings, itemsToPreview, glossaryBlueprints, types, tags, allItems);
          setPreviewOutput(preview);
        } catch (error) {
          console.error('Error generating preview:', error);
          setPreviewOutput('// Error generating preview');
        }
      }
    } else {
      setPreviewOutput('');
    }
  }, [selectedPreviewItems, template, allItems, glossaryBlueprints, categoryFieldMappings, types, tags]);

  const handleSave = async () => {
    if (!selectedGame) return;

    setIsSaving(true);
    try {
      // Add updated template with category field mappings
      const updatedTemplate = {
        ...template,
        categoryFieldMappings
      };

      // Only save this template if it's active, otherwise keep the current active template
      const currentConfig = (selectedGame.settings?.exportTemplates as ExportTemplatesConfig) || {
        templates: [],
        activeTemplateId: null
      };

      let newConfig: ExportTemplatesConfig;
      
      if (isActive) {
        // If this template is active, save only this one
        newConfig = {
          templates: [updatedTemplate],
          activeTemplateId: template.id
        };
      } else {
        // If not active, keep the current configuration unchanged
        newConfig = currentConfig;
      }

      console.log('💾 Saving database configuration to backend:', {
        gameId: selectedGame.id,
        isActive,
        categoryMappings: Object.keys(template.categoryMapping || {}).length,
        fieldMappings: Object.keys(template.fieldExportMappings || {}).length,
        config: newConfig
      });

      const result = await updateGameInfo(
        selectedGame.id,
        {
          settings: {
            ...selectedGame.settings,
            exportTemplates: newConfig
          }
        },
        toast
      );

      console.log('✅ Database configuration saved successfully:', result);

      // Refresh data to get updated game settings
      await refreshData();

      toast({
        title: 'Configuration Saved',
        description: 'Database export configuration has been saved successfully to the backend'
      });
    } catch (error) {
      console.error('❌ Failed to save database configuration:', error);
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedGame) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Please select a project to configure database exports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Database Export</h2>
          <p className="text-muted-foreground mt-1">
            Configure how library objects are exported to database format. Customize structure and field mappings.
          </p>
          {selectedGame?.settings?.engine && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-muted-foreground">Engine:</span>
              <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                {selectedGame.settings.engine}
                {selectedGame.settings.codeLanguage && ` / ${selectedGame.settings.codeLanguage.toUpperCase()}`}
              </span>
              <span className="text-xs text-muted-foreground">→ Using</span>
              <span className="px-2 py-1 bg-accent text-accent-foreground rounded text-xs font-medium">
                {template.platform === 'unity' ? 'C# Format' : 
                 template.platform === 'roblox' ? 'Lua Format' : 
                 `${template.platform.toUpperCase()} Format`}
              </span>
            </div>
          )}
        </div>
        <Button onClick={handleSave} disabled={isSaving} className="bg-primary hover:bg-primary/90">
          <IconProvider iconstring="fa-save" size={14} className="mr-2" />
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      {/* Template Card */}
      <div className="border border-border rounded-lg bg-card">
        {/* Template Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{template.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={isActive}
                onCheckedChange={(checked) => setIsActive(!!checked)}
                id="activateDatabase"
              />
              <Label htmlFor="activateDatabase" className="cursor-pointer font-medium text-sm">
                Set Active
              </Label>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium uppercase">
              {template.platform}
            </span>
            <span className="px-2 py-1 bg-accent rounded text-xs font-medium">
              Preset
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex px-4">
            <button
              onClick={() => setActiveTab('config')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'config'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'categories'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Category Mapping
            </button>
            <button
              onClick={() => setActiveTab('fields')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'fields'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Field Mappings
            </button>
            <button
              onClick={() => setActiveTab('output')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'output'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Output File
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Configuration Tab */}
          {activeTab === 'config' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Export Options</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 border border-border rounded-lg">
                    <Checkbox
                      checked={template.options?.ignoreSections || false}
                      onCheckedChange={(checked) => 
                        updateField('options', { 
                          ...template.options, 
                          ignoreSections: !!checked 
                        })
                      }
                      id="ignoreSections"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label htmlFor="ignoreSections" className="cursor-pointer font-medium">
                        Ignore Sections (Export fields directly)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        When enabled, all fields from all sections are exported in a flat structure without section grouping.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 border border-border rounded-lg">
                    <Checkbox
                      checked={template.options?.stripHtml !== false}
                      onCheckedChange={(checked) => 
                        updateField('options', { 
                          ...template.options, 
                          stripHtml: !!checked 
                        })
                      }
                      id="stripHtml"
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label htmlFor="stripHtml" className="cursor-pointer font-medium">
                        Strip HTML (Plain text export)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        When enabled, HTML tags are removed from text fields, producing clean plain text output.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Filename Pattern</h4>
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <IconProvider iconstring="fa-file-code" size={16} />
                  <span className="font-mono text-sm">{template.instanceFile.filename}</span>
                  {template.platform === 'unity' && (
                    <span className="text-xs text-muted-foreground ml-auto">C# File</span>
                  )}
                  {template.platform === 'roblox' && (
                    <span className="text-xs text-muted-foreground ml-auto">Lua File</span>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Location in Package</h4>
                <Input
                  value={template.instanceFile.location || '/'}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>
          )}

          {/* Category Mapping Tab */}
          {activeTab === 'categories' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Map database categories to template names (e.g., "Quests" → "Quest Template V2"). Objects from the selected template will be grouped under the category name in the exported Lua file.
              </p>
              
              <div className="space-y-2">
                {template.categoryMapping && Object.entries(template.categoryMapping).map(([templateName, categoryName]) => (
                  <div key={templateName} className="flex items-center gap-2 p-2 border border-border rounded-lg">
                    <Input
                      value={categoryName}
                      onChange={(e) => {
                        const newMapping = { ...template.categoryMapping };
                        const oldCategoryName = newMapping[templateName];
                        delete newMapping[templateName];
                        newMapping[templateName] = e.target.value;
                        updateField('categoryMapping', newMapping);
                      }}
                      className="flex-1"
                      placeholder="Category Name (e.g., Quests)"
                    />
                    <IconProvider iconstring="fa-arrow-right" size={14} className="text-muted-foreground" />
                    <Select
                      value={templateName}
                      onValueChange={(newTemplateName) => {
                        const newMapping = { ...template.categoryMapping };
                        const categoryValue = newMapping[templateName];
                        delete newMapping[templateName];
                        newMapping[newTemplateName] = categoryValue;
                        updateField('categoryMapping', newMapping);
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select Template" />
                      </SelectTrigger>
                      <SelectContent>
                        {glossaryBlueprints && glossaryBlueprints.length > 0 ? (
                          glossaryBlueprints.map((blueprint) => (
                            <SelectItem key={blueprint.id} value={blueprint.name}>
                              {blueprint.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-templates" disabled>
                            No templates available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newMapping = { ...template.categoryMapping };
                        delete newMapping[templateName];
                        updateField('categoryMapping', newMapping);
                      }}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <IconProvider iconstring="fa-trash" size={12} />
                    </Button>
                  </div>
                ))}
                
                {/* Add New Mapping */}
                <div className="flex items-center gap-2 pt-4 border-t border-border">
                  <Input
                    placeholder="Category Name (e.g., Quests)"
                    id="newCategoryName"
                    className="flex-1"
                  />
                  <IconProvider iconstring="fa-arrow-right" size={14} className="text-muted-foreground" />
                  <Select
                    value=""
                    onValueChange={(templateName) => {
                      const categoryInput = document.getElementById('newCategoryName') as HTMLInputElement;
                      if (categoryInput && categoryInput.value && templateName) {
                        const newMapping = { 
                          ...template.categoryMapping,
                          [templateName]: categoryInput.value
                        };
                        updateField('categoryMapping', newMapping);
                        categoryInput.value = '';
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select Template" />
                    </SelectTrigger>
                    <SelectContent>
                      {glossaryBlueprints && glossaryBlueprints.length > 0 ? (
                        glossaryBlueprints
                          .filter(blueprint => !template.categoryMapping?.[blueprint.name])
                          .map((blueprint) => (
                            <SelectItem key={blueprint.id} value={blueprint.name}>
                              {blueprint.name}
                            </SelectItem>
                          ))
                      ) : (
                        <SelectItem value="no-templates" disabled>
                          No templates available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="default"
                    size="sm"
                    disabled={!glossaryBlueprints || glossaryBlueprints.length === 0}
                    onClick={() => {
                      // This is now handled by the Select onValueChange
                    }}
                  >
                    <IconProvider iconstring="fa-plus" size={12} className="mr-2" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Field Mappings Tab */}
          {activeTab === 'fields' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select a category to configure how its template fields should be exported. Each field can be customized for export behavior.
              </p>

              {/* Category Selection */}
              <div>
                <Label className="text-sm mb-2 block">Select Category</Label>
                <Select
                  value={selectedCategoryForMapping}
                  onValueChange={(value) => {
                    setSelectedCategoryForMapping(value);
                    setPreviewItemForMapping(''); // Reset preview item when category changes
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a category to configure field mappings" />
                  </SelectTrigger>
                  <SelectContent>
                    {template.categoryMapping && Object.entries(template.categoryMapping).length > 0 ? (
                      Object.entries(template.categoryMapping).map(([templateName, categoryName]) => (
                        <SelectItem key={categoryName} value={categoryName}>
                          <div className="flex items-center gap-2">
                            <IconProvider iconstring="fa-folder" size={12} />
                            <span>{categoryName}</span>
                            <span className="text-xs text-muted-foreground">({templateName})</span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-categories" disabled>
                        No categories configured
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Template Fields Table */}
              {selectedCategoryForMapping ? (() => {
                // Find the template name for this category
                const templateName = Object.entries(template.categoryMapping || {}).find(
                  ([, catName]) => catName === selectedCategoryForMapping
                )?.[0];
                
                // Find the blueprint for this template
                const blueprint = glossaryBlueprints?.find(bp => bp.name === templateName);
                
                
                if (!blueprint) {
                  return (
                    <div className="text-center py-8 text-muted-foreground text-sm border border-border rounded-lg">
                      <IconProvider iconstring="fa-exclamation-triangle" size={16} className="mx-auto mb-2" />
                      <p>Template not found for this category</p>
                      <p className="text-xs mt-2">Template name: {templateName}</p>
                      <p className="text-xs">Available templates: {glossaryBlueprints?.map(bp => bp.name).join(', ')}</p>
                    </div>
                  );
                }

                // Extract fields from blueprint data (handle multiple data structures)
                const fields: Array<{ id: string; label: string; type: string }> = [];
                
                if (blueprint.data && typeof blueprint.data === 'object') {
                  // Get sections data - could be in .sections property or directly in data
                  const sectionsData = (blueprint.data as any).sections || blueprint.data;
                  const elementOrder = (blueprint.data as any).elementOrder;
                  
                  // Case 1: Data is directly an array of sections
                  if (Array.isArray(blueprint.data)) {
                    blueprint.data.forEach((section: any) => {
                      if (section.fields && Array.isArray(section.fields)) {
                        section.fields.forEach((field: any) => {
                          fields.push({
                            id: field.id,
                            label: field.label || field.id,
                            type: field.type || 'text'
                          });
                        });
                      }
                    });
                  }
                  // Case 2: Sections property is an array
                  else if (Array.isArray((blueprint.data as any).sections)) {
                    (blueprint.data as any).sections.forEach((section: any) => {
                      if (section.fields && Array.isArray(section.fields)) {
                        section.fields.forEach((field: any) => {
                          fields.push({
                            id: field.id,
                            label: field.label || field.id,
                            type: field.type || 'text'
                          });
                        });
                      }
                    });
                  }
                  // Case 3: Object structure with section IDs as keys (new format)
                  else if (typeof sectionsData === 'object') {
                    // Get sections in order if elementOrder exists
                    const sectionKeys = elementOrder && Array.isArray(elementOrder) 
                      ? elementOrder.filter((key: string) => sectionsData[key] && !key.startsWith('field_'))
                      : Object.keys(sectionsData).filter(key => !key.startsWith('field_') && !key.startsWith('_'));
                    
                    sectionKeys.forEach((sectionKey: string) => {
                      const sectionData = sectionsData[sectionKey];
                      
                      // Case 3a: Section has fields array directly
                      if (sectionData && sectionData.fields && Array.isArray(sectionData.fields)) {
                        sectionData.fields.forEach((field: any) => {
                          fields.push({
                            id: field.id,
                            label: field.label || field.id,
                            type: field.type || 'text'
                          });
                        });
                      }
                      // Case 3b: Section has fieldIds that reference field definitions
                      else if (sectionData && sectionData.fieldIds && Array.isArray(sectionData.fieldIds)) {
                        // For each field ID, look up the field definition
                        sectionData.fieldIds.forEach((fieldId: string) => {
                          const fieldKey = `field_${fieldId}`;
                          const fieldData = sectionsData[fieldKey];
                          
                          if (fieldData) {
                            fields.push({
                              id: fieldData.id || fieldId,
                              label: fieldData.label || fieldData.id || fieldId,
                              type: fieldData.type || 'text'
                            });
                          }
                        });
                      }
                      // Case 3c: Section has _fieldOrder array with field objects as direct properties
                      else if (sectionData && sectionData._fieldOrder && Array.isArray(sectionData._fieldOrder)) {
                        sectionData._fieldOrder.forEach((fieldId: string) => {
                          const fieldData = sectionData[fieldId];
                          if (fieldData && typeof fieldData === 'object' && fieldData.id) {
                            fields.push({
                              id: fieldData.id,
                              label: fieldData.label || fieldData.id,
                              type: fieldData.type || 'text'
                            });
                          }
                        });
                      }
                    });
                  }
                }
                
                if (fields.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground text-sm border border-border rounded-lg">
                      <IconProvider iconstring="fa-info-circle" size={16} className="mx-auto mb-2" />
                      <p>No fields found in this template</p>
                      <p className="text-xs mt-2">Template: {blueprint.name}</p>
                      <p className="text-xs">Check console for debug info</p>
                      {process.env.NODE_ENV === 'development' && (
                        <details className="mt-2 text-left">
                          <summary className="cursor-pointer">Debug Info</summary>
                          <pre className="text-xs mt-1 overflow-auto max-h-40">
                            {JSON.stringify({
                              blueprintId: blueprint.id,
                              blueprintName: blueprint.name,
                              dataType: typeof blueprint.data,
                              dataKeys: blueprint.data && typeof blueprint.data === 'object' ? Object.keys(blueprint.data) : [],
                              isArray: Array.isArray(blueprint.data),
                            }, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  );
                }

                // Get current mappings for this category
                const currentMappings = categoryFieldMappings[selectedCategoryForMapping] || {};

                return (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <IconProvider iconstring="fa-folder" size={14} />
                            <span className="font-medium text-sm">{selectedCategoryForMapping}</span>
                            <span className="text-xs text-muted-foreground">({blueprint.name})</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {fields.length} fields available for mapping
                          </p>
                        </div>
                        {/* Preview object selector */}
                        {(() => {
                          const categoryItems = allItems?.filter(item => {
                            const itemBlueprint = glossaryBlueprints?.find(bp => bp.id === item.blueprint_id);
                            return itemBlueprint?.name === templateName;
                          }) || [];
                          
                          return categoryItems.length > 0 ? (
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-muted-foreground">Preview:</Label>
                              <Select
                                value={previewItemForMapping}
                                onValueChange={(value) => setPreviewItemForMapping(value)}
                              >
                                <SelectTrigger className="w-48 h-8 text-xs">
                                  <SelectValue placeholder="Select object to preview" />
                                </SelectTrigger>
                                <SelectContent>
                                  {categoryItems.map((item) => (
                                    <SelectItem key={item.id} value={item.id}>
                                      {item.name || item.id}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left p-3 font-medium text-sm">Field Name</th>
                          <th className="text-left p-3 font-medium text-sm w-52">Export As</th>
                          <th className="text-center p-3 font-medium text-sm w-20">Use</th>
                          <th className="text-left p-3 font-medium text-sm">Field Type</th>
                          <th className="text-left p-3 font-medium text-sm">Export Behavior</th>
                          {previewItemForMapping && <th className="text-left p-3 font-medium text-sm">Preview Result</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field, index) => {
                          // Helper to strip HTML tags from text
                          const stripHtml = (html: string): string => {
                            if (!html || typeof html !== 'string') return html;
                            let text = html
                              .replace(/<br\s*\/?>/gi, '\n')
                              .replace(/<\/p>/gi, '\n')
                              .replace(/<\/div>/gi, '\n')
                              .replace(/<\/li>/gi, '\n')
                              .replace(/<\/h[1-6]>/gi, '\n');
                            text = text.replace(/<[^>]*>/g, '');
                            text = text
                              .replace(/&nbsp;/gi, ' ')
                              .replace(/&amp;/gi, '&')
                              .replace(/&lt;/gi, '<')
                              .replace(/&gt;/gi, '>')
                              .replace(/&quot;/gi, '"')
                              .replace(/&#39;/gi, "'")
                              .replace(/&apos;/gi, "'");
                            return text.replace(/\n{3,}/g, '\n\n').trim();
                          };
                          
                          // Helper to apply text case transformation based on export behavior
                          const applyTextCase = (text: string, exportBehavior: string): string => {
                            if (!text || typeof text !== 'string') return text;
                            // Parse text case from export behavior (e.g., 'value:uppercase')
                            const textCase = exportBehavior.includes(':') 
                              ? exportBehavior.split(':')[1] 
                              : 'original';
                            switch (textCase) {
                              case 'uppercase':
                                return text.toUpperCase();
                              case 'lowercase':
                                return text.toLowerCase();
                              case 'original':
                              default:
                                return text;
                            }
                          };
                          
                          // Get preview value for this field
                          const getPreviewValue = () => {
                            if (!previewItemForMapping) return null;
                            const previewItem = allItems?.find(item => item.id === previewItemForMapping);
                            if (!previewItem || !previewItem.data) return null;
                            
                            // Find the field value in the item's data
                            let fieldValue: any = null;
                            if (Array.isArray(previewItem.data)) {
                              // Array of sections with fields
                              for (const section of previewItem.data) {
                                if (section.fields && Array.isArray(section.fields)) {
                                  const foundField = section.fields.find((f: any) => f.id === field.id);
                                  if (foundField) {
                                    fieldValue = foundField.value;
                                    break;
                                  }
                                }
                              }
                            }
                            
                            if (fieldValue === null || fieldValue === undefined) return <span className="text-muted-foreground italic">No value</span>;
                            
                            // Get the export behavior for this field
                            const exportBehavior = currentMappings[field.id] || getDefaultExportValue(field.type);
                            
                            // Apply HTML stripping for text fields if option is enabled (default: true)
                            if (template.options?.stripHtml !== false) {
                              if (field.type === 'richtext' || field.type === 'textarea' || field.type === 'text') {
                                if (typeof fieldValue === 'string') {
                                  fieldValue = stripHtml(fieldValue);
                                }
                              }
                            }
                            
                            // Apply text case transformation for text fields based on per-field export behavior
                            if (field.type === 'richtext' || field.type === 'textarea' || field.type === 'text' || field.type === 'title') {
                              if (typeof fieldValue === 'string') {
                                fieldValue = applyTextCase(fieldValue, exportBehavior);
                              }
                            }
                            
                            // Parse property and text case from export behavior (e.g., 'name:uppercase' -> { property: 'name', textCase: 'uppercase' })
                            const parseExportBehavior = (behavior: string): { property: string; textCase: string } => {
                              if (behavior.includes(':')) {
                                const [property, textCase] = behavior.split(':');
                                return { property, textCase };
                              }
                              return { property: behavior, textCase: 'original' };
                            };
                            
                            const { property: exportProperty, textCase: exportTextCase } = parseExportBehavior(exportBehavior);
                            
                            // Helper to resolve reference by ID based on field type
                            const resolveReference = (refValue: any, fieldType: string): any => {
                              // If it's just an ID string, look it up
                              const refId = typeof refValue === 'string' ? refValue : refValue?.id;
                              
                              if (!refId) return refValue;
                              
                              let resolvedItem: any = null;
                              
                              if (fieldType === 'types') {
                                // Types store contains TypeItem objects, each with a data array of TypeElement objects
                                // The field stores a TypeElement.id, so we need to search through all type elements
                                for (const typeItem of (types || [])) {
                                  if (typeItem.data && Array.isArray(typeItem.data)) {
                                    const element = typeItem.data.find(el => el.id === refId);
                                    if (element) {
                                      resolvedItem = element;
                                      break;
                                    }
                                  }
                                }
                              } else if (fieldType === 'tags') {
                                // Similar structure for tags - search through tag elements
                                for (const tagItem of (tags || [])) {
                                  if (tagItem.data && Array.isArray(tagItem.data)) {
                                    const element = tagItem.data.find((el: any) => el.id === refId);
                                    if (element) {
                                      resolvedItem = element;
                                      break;
                                    }
                                  }
                                }
                              } else if (fieldType === 'glossaryItem') {
                                resolvedItem = allItems?.find(i => i.id === refId);
                              }
                              
                              if (resolvedItem) {
                                let result = resolvedItem[exportProperty] || resolvedItem.name || resolvedItem.id || refId;
                                // Apply text case transformation
                                if (typeof result === 'string') {
                                  result = applyTextCase(result, exportBehavior);
                                }
                                return result;
                              }
                              
                              // If we couldn't resolve, try the original value
                              if (typeof refValue === 'object' && refValue !== null) {
                                let result = refValue[exportProperty] || refValue.name || refValue.id || JSON.stringify(refValue);
                                if (typeof result === 'string') {
                                  result = applyTextCase(result, exportBehavior);
                                }
                                return result;
                              }
                              
                              return refId;
                            };
                            
                            // Handle reference field types (types, tags, glossaryItem)
                            if (field.type === 'types' || field.type === 'tags' || field.type === 'glossaryItem') {
                              if (Array.isArray(fieldValue)) {
                                const mappedValues = fieldValue.map(item => resolveReference(item, field.type));
                                return <span className="text-xs font-mono">[{mappedValues.join(', ')}]</span>;
                              } else {
                                const result = resolveReference(fieldValue, field.type);
                                return <span className="text-xs font-mono">{String(result)}</span>;
                              }
                            }
                            
                            // Handle other object/reference fields
                            if (typeof fieldValue === 'object' && fieldValue !== null) {
                              if (Array.isArray(fieldValue)) {
                                const mappedValues = fieldValue.map(item => {
                                  if (typeof item === 'object' && item !== null) {
                                    return item[exportBehavior] || item.name || item.id || JSON.stringify(item);
                                  }
                                  return String(item);
                                });
                                return <span className="text-xs font-mono">[{mappedValues.join(', ')}]</span>;
                              } else {
                                const result = fieldValue[exportBehavior] || fieldValue.name || fieldValue.id || JSON.stringify(fieldValue);
                                return <span className="text-xs font-mono">{String(result)}</span>;
                              }
                            }
                            
                            return <span className="text-xs font-mono">{String(fieldValue)}</span>;
                          };
                          
                          // Get field name override for this field
                          const fieldOverride = template.fieldNameOverrides?.[blueprint.id]?.[field.label] || {
                            exportName: field.label,
                            useExportName: false
                          };
                          
                          return (
                            <tr key={field.id} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                              <td className="p-3 text-sm font-medium">{field.label}</td>
                              <td className="p-3">
                                <Input
                                  value={fieldOverride.exportName || field.label}
                                  onChange={(e) => {
                                    const newOverrides = {
                                      ...template.fieldNameOverrides,
                                      [blueprint.id]: {
                                        ...template.fieldNameOverrides?.[blueprint.id],
                                        [field.label]: {
                                          ...fieldOverride,
                                          exportName: e.target.value
                                        }
                                      }
                                    };
                                    updateField('fieldNameOverrides', newOverrides);
                                  }}
                                  placeholder={field.label}
                                  className="font-mono text-xs h-8"
                                  disabled={!fieldOverride.useExportName}
                                />
                              </td>
                              <td className="p-3 text-center">
                                <Checkbox
                                  checked={fieldOverride.useExportName}
                                  onCheckedChange={(checked) => {
                                    const newOverrides = {
                                      ...template.fieldNameOverrides,
                                      [blueprint.id]: {
                                        ...template.fieldNameOverrides?.[blueprint.id],
                                        [field.label]: {
                                          ...fieldOverride,
                                          useExportName: checked as boolean
                                        }
                                      }
                                    };
                                    updateField('fieldNameOverrides', newOverrides);
                                  }}
                                />
                              </td>
                              <td className="p-3 text-sm">
                                <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                                  {getFieldTypeDisplayName(field.type)}
                                </span>
                              </td>
                              <td className="p-3">
                                <select
                                  value={currentMappings[field.id] || getDefaultExportValue(field.type)}
                                  onChange={(e) => {
                                    setCategoryFieldMappings(prev => ({
                                      ...prev,
                                      [selectedCategoryForMapping]: {
                                        ...prev[selectedCategoryForMapping],
                                        [field.id]: e.target.value
                                      }
                                    }));
                                  }}
                                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm"
                                >
                                  {getExportOptions(field.type).map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              {previewItemForMapping && (
                                <td className="p-3 text-sm max-w-48 truncate">
                                  {getPreviewValue()}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })() : (
                <div className="text-center py-12 text-muted-foreground text-sm border border-border rounded-lg border-dashed">
                  <IconProvider iconstring="fa-arrow-up" size={20} className="mx-auto mb-3 opacity-50" />
                  <p>Select a category above to configure field mappings</p>
                  <p className="text-xs mt-1">Field mappings determine how template fields are exported to Lua</p>
                </div>
              )}

              {/* Global Field Type Mappings - Keep as fallback */}
              <div className="pt-6 border-t border-border">
                <h4 className="font-semibold mb-2 text-sm">Global Field Type Mappings (Fallback)</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  These mappings apply to reference fields when no specific field mapping is configured above.
                </p>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 font-medium text-sm">Field Type</th>
                        <th className="text-left p-3 font-medium text-sm">Export Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['glossaryItem', 'types', 'tags', 'file', 'image'].map((fieldType, index) => (
                        <tr key={fieldType} className={index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                          <td className="p-3 capitalize text-sm">{fieldType}</td>
                          <td className="p-3">
                            <select
                              value={template.fieldExportMappings?.[fieldType] || 'name'}
                              onChange={(e) => {
                                const newMappings = { 
                                  ...template.fieldExportMappings,
                                  [fieldType]: e.target.value
                                };
                                updateField('fieldExportMappings', newMappings);
                              }}
                              className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm"
                            >
                              <option value="name">Name</option>
                              <option value="id">ID</option>
                              <option value="value">Value</option>
                              <option value="label">Label</option>
                              {(fieldType === 'image' || fieldType === 'file') && (
                                <option value="url">URL</option>
                              )}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Output File Tab */}
          {activeTab === 'output' && (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Output Configuration</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <IconProvider iconstring="fa-file-code" size={16} />
                      <span className="font-mono text-sm font-medium">{template.instanceFile.filename}</span>
                    </div>
                    {template.platform === 'unity' && (
                      <span className="text-xs text-muted-foreground">C# File</span>
                    )}
                    {template.platform === 'roblox' && (
                      <span className="text-xs text-muted-foreground">Lua File</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    All library objects will be exported to this single {template.platform === 'unity' ? 'C#' : 'Lua'} file, organized by category.
                  </p>
                </div>
              </div>

              {/* Debug Info Panel */}
              {process.env.NODE_ENV === 'development' && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs">
                  <div className="font-semibold mb-1">🐛 Debug Info:</div>
                  <div>📚 Total Library Objects: {allItems?.length || 0}</div>
                  <div>📋 Total Templates: {glossaryBlueprints?.length || 0}</div>
                  <div>🔗 Library Objects linked to templates: {allItems?.filter(i => i.blueprint_id).length || 0}</div>
                  <div className="mt-2">
                    <details>
                      <summary className="cursor-pointer font-semibold">📋 Show all available templates</summary>
                      <div className="mt-1 ml-2 space-y-1">
                        {glossaryBlueprints && glossaryBlueprints.length > 0 ? (
                          glossaryBlueprints.map(template => (
                            <div key={template.id} className="font-mono">
                              • "{template.name}"
                            </div>
                          ))
                        ) : (
                          <div className="text-red-600">No templates loaded!</div>
                        )}
                      </div>
                    </details>
                  </div>
                  <div className="mt-2">
                    <details>
                      <summary className="cursor-pointer font-semibold">📚 Show library objects and their templates</summary>
                      <div className="mt-1 ml-2 max-h-60 overflow-y-auto space-y-1">
                        {allItems && allItems.length > 0 ? (
                          allItems.slice(0, 15).map(item => {
                            const template = glossaryBlueprints?.find(bp => bp.id === item.blueprint_id);
                            return (
                              <div key={item.id} className={!template ? "text-orange-600" : ""}>
                                • <span className="font-semibold">{item.name}</span>
                                {template ? (
                                  <span> → Template: "<span className="font-mono">{template.name}</span>"</span>
                                ) : (
                                  <span className="text-red-600"> → ⚠️ No template linked</span>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-red-600">No library objects loaded!</div>
                        )}
                        {allItems && allItems.length > 15 && (
                          <div className="text-muted-foreground italic">... and {allItems.length - 15} more</div>
                        )}
                      </div>
                    </details>
                  </div>
                  <div className="mt-2">
                    <details>
                      <summary className="cursor-pointer font-semibold">🗂️ Show your category mappings</summary>
                      <div className="mt-1 ml-2 space-y-1">
                        {template.categoryMapping && Object.keys(template.categoryMapping).length > 0 ? (
                          Object.entries(template.categoryMapping).map(([templateName, categoryName]) => (
                            <div key={templateName}>
                              • Looking for template "<span className="font-mono font-semibold">{templateName}</span>" → Export as "{categoryName}"
                            </div>
                          ))
                        ) : (
                          <div className="text-orange-600">No category mappings configured!</div>
                        )}
                      </div>
                    </details>
                  </div>
                </div>
              )}

              {/* Categories That Will Be Created */}
              <div>
                <h4 className="font-semibold mb-2">Export Structure</h4>
                {template.categoryMapping && Object.keys(template.categoryMapping).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(template.categoryMapping).map(([templateName, categoryName]) => {
                      // Count items for this template
                      const itemCount = allItems?.filter(item => {
                        const itemBlueprint = glossaryBlueprints?.find(bp => bp.id === item.blueprint_id);
                        const matches = itemBlueprint?.name === templateName;
                        
                        return matches;
                      }).length || 0;

                      return (
                        <div key={templateName} className="flex items-center justify-between p-3 border border-border rounded-lg">
                          <div className="flex items-center gap-3">
                            <IconProvider iconstring="fa-folder" size={14} className="text-primary" />
                            <div>
                              <div className="font-medium text-sm">{categoryName}</div>
                              <div className="text-xs text-muted-foreground">
                                From template: {templateName}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {itemCount} {itemCount === 1 ? 'object' : 'objects'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground text-sm border border-border rounded-lg">
                    <IconProvider iconstring="fa-info-circle" size={16} className="mx-auto mb-2" />
                    <p>No category mappings configured</p>
                    <p className="text-xs mt-1">Add mappings in the Category Mapping tab</p>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-semibold mb-2">Export Behavior</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <IconProvider iconstring="fa-check" size={12} className="mt-1 text-primary" />
                    <span>Groups all objects by template type into categories</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <IconProvider iconstring="fa-check" size={12} className="mt-1 text-primary" />
                    <span>Exports to a single {template.instanceFile.filename} file</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <IconProvider iconstring="fa-check" size={12} className="mt-1 text-primary" />
                    <span>Automatically formats values based on field types</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <IconProvider iconstring="fa-check" size={12} className="mt-1 text-primary" />
                    <span>Uses configured field mappings for reference fields</span>
                  </li>
                </ul>
              </div>

              {/* Show Example Button */}
              <div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPreview(!showPreview);
                    // Auto-select some items if none selected and preview is being shown
                    if (!showPreview && selectedPreviewItems.length === 0 && allItems && allItems.length > 0) {
                      setSelectedPreviewItems(allItems.slice(0, 3).map(item => item.id));
                    }
                  }}
                  className="w-full"
                >
                  <IconProvider 
                    iconstring={showPreview ? "fa-eye-slash" : "fa-eye"} 
                    size={14} 
                    className="mr-2" 
                  />
                  {showPreview ? 'Hide Example' : 'Show Example'}
                </Button>
              </div>

              {/* Preview Section - Collapsible */}
              {showPreview && (
                <div className="space-y-4 pt-2 border-t border-border">
                  <div>
                    <h4 className="font-semibold mb-2">Export Preview</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Select objects to preview how they will be exported with the current configuration.
                    </p>

                    {/* Item Selection */}
                    {allItems && allItems.length > 0 ? (
                      <>
                        <div className="mb-3">
                          <Label className="text-sm mb-2 block">Select Objects for Preview</Label>
                          <div className="max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1">
                            {allItems.slice(0, 20).map(item => (
                              <div key={item.id} className="flex items-center gap-2 p-1 hover:bg-muted rounded">
                                <Checkbox
                                  checked={selectedPreviewItems.includes(item.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedPreviewItems(prev => [...prev, item.id]);
                                    } else {
                                      setSelectedPreviewItems(prev => prev.filter(id => id !== item.id));
                                    }
                                  }}
                                  id={`preview-${item.id}`}
                                />
                                <Label htmlFor={`preview-${item.id}`} className="cursor-pointer text-sm flex-1">
                                  {item.name || item.id}
                                </Label>
                              </div>
                            ))}
                          </div>
                          {allItems.length > 20 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Showing first 20 objects. {allItems.length - 20} more available.
                            </p>
                          )}
                        </div>

                        {/* Preview Output */}
                        {previewOutput && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-sm">Preview Output</Label>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(previewOutput);
                                }}
                              >
                                <IconProvider iconstring="fa-copy" size={12} className="mr-2" />
                                Copy
                              </Button>
                            </div>
                            <div className="relative">
                              <pre className="bg-muted/50 border border-border rounded-lg p-4 text-xs overflow-x-auto max-h-96 overflow-y-auto font-mono">
                                <code>{previewOutput}</code>
                              </pre>
                            </div>
                          </div>
                        )}

                        {selectedPreviewItems.length === 0 && (
                          <div className="text-center py-8 text-muted-foreground text-sm">
                            <IconProvider iconstring="fa-info-circle" size={16} className="mx-auto mb-2" />
                            <p>Select objects above to see export preview</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        <IconProvider iconstring="fa-info-circle" size={16} className="mx-auto mb-2" />
                        <p>No objects available in the library for preview</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
